import { PIX_KEY_TYPE_BY_LEGACY, type PixKeyTypeEnum, resolvePixKeyType } from "@/shared/utils/pixKey";

import { resolveColRail, resolveRuntimeCorridor } from "../constants/dispersions.constants";
import { resolveCbuCvuKind } from "../services/paymentsPayout.service";

/** What the client-account form needs to describe the destination that was just paid. */
export interface RecipientAccountDraft {
  /** Account currency — the corridor's, not the wallet's. */
  currency: string;
  /** `bank` option of that currency (`PAYMENT_METHODS_BY_CURRENCY`), which selects the field set. */
  bank: string;
  /** Values for `FIELDS_BY_CURRENCY_METHOD["<currency>|<bank>"]`, by field id. */
  methodFields: Record<string, string>;
}

/**
 * The account form's Pix select stores a numeric code, not the enum. Derived from the legacy table rather
 * than written again, so the two can never disagree about which number is CNPJ.
 */
const PIX_SELECT_CODE_BY_TYPE = Object.fromEntries(
  Object.entries(PIX_KEY_TYPE_BY_LEGACY)
    .filter(([legacy]) => /^\d$/.test(legacy))
    .map(([legacy, type]) => [type, legacy]),
) as Record<PixKeyTypeEnum, string>;

/**
 * The destination of a finished payout, expressed as a client account — what "save this recipient" has to
 * hand the account form so nobody retypes the key they just paid.
 *
 * It is a translation between two vocabularies that were built years apart: the wizard's per-corridor
 * `recipientFields` and the account form's `(currency, bank) → field ids` matrix. Null means this destination
 * has no account shape to save, and the offer is simply not made — true of Colombia's Transfiya rail, whose
 * bank + account + document triplet is not among the four COP methods an account can hold.
 *
 * Returning null is also the honest answer for an incomplete destination. The alternative — half-filling the
 * form — saves an account that looks registered and fails at the next payout.
 */
export function recipientAccountDraft(
  destination: string,
  recipientFields: Record<string, unknown> | null | undefined,
): RecipientAccountDraft | null {
  const read = (field: string) => String(recipientFields?.[field] ?? "").trim();

  switch (resolveRuntimeCorridor(destination)) {
    case "BRA": {
      const key = read("pix_key");
      const type = resolvePixKeyType(read("pix_type"));
      if (!key || !type) return null;
      return {
        currency: "BRL",
        bank: "Pix",
        methodFields: { pix_type: PIX_SELECT_CODE_BY_TYPE[type], pix_key: key },
      };
    }
    case "MEX": {
      const clabe = read("clabe").replace(/\D/g, "");
      if (clabe.length !== 18) return null;
      return { currency: "MXN", bank: "SPEI", methodFields: { clabe } };
    }
    case "ARG": {
      const accountNumber = read("account_number").replace(/\D/g, "");
      if (accountNumber.length !== 22) return null;
      // The two are separate methods with separate field ids, and the number alone cannot tell them apart —
      // so the kind comes from the SHARED resolver, the same one the payout payload goes through. Deciding
      // it here with its own ternary is how the account gets saved under a field the payout did not use.
      // A value that is neither is null, not a CVU: the payout builder throws on it, so it never paid.
      const kind = resolveCbuCvuKind(recipientFields?.account_type);
      if (!kind) return null;
      return { currency: "ARS", bank: kind, methodFields: { [kind.toLowerCase()]: accountNumber } };
    }
    case "COL": {
      if (resolveColRail(recipientFields) !== "BREB") return null;
      const key = read("breb_key_value");
      if (!key) return null;
      // The key's TYPE is deliberately dropped: a stored account holds the value only, which is why reading
      // one back has to infer the type from its shape (`inferBrebKeyType`). Saving a type the account cannot
      // carry would invent a field.
      return { currency: "COP", bank: "Bre-B", methodFields: { llaveBreB: key } };
    }
    default:
      return null;
  }
}
